import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  Navbar,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Card
} from "react-bootstrap";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";

import FlightList from "./components/FlightList";
import Footer from "./components/Footer";
import About from "./pages/About";
import Legal from "./pages/Legal";
import { AIRPORTS, GERMAN_IATAS } from "./airportData";

import "./App.css";

// Basis-URL aus ENV oder Fallback
const API_BASE = process.env.REACT_APP_API_BASE || "https://api.fluybuy.de";

function SearchPage() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({
    airport:      "",
    min_days:     3,
    max_days:     6,
    budget:       100,
    departure_at: "",
    hotel_budget: ""    // ← neu: Hotelbudget optional
  });

  const handleChange = (e) =>
    setParams({ ...params, [e.target.name]: e.target.value });

  const searchFlights = async () => {
    setLoading(true);

    // Anfrage-Parameter zusammenbauen
    const q = {
      airport:  params.airport,
      min_days: params.min_days,
      max_days: params.max_days,
      budget:   params.budget
    };
    if (params.departure_at)  q.departure_at  = params.departure_at;
    if (params.hotel_budget)  q.hotel_budget  = params.hotel_budget; // optional hotel budget

    try {
      const qs  = new URLSearchParams(q).toString();
      const res = await axios.get(`${API_BASE}/search?${qs}`);
      setFlights(res.data);
    } catch {
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  // Animation-Parameter
  const logoSize    = 120;
  const orbitRadius = logoSize * 0.7;

  return (
    <Container className="mt-4">
      {/* Suchformular */}
      <Card className="search-card mb-4 p-4 shadow-sm">
        <Card.Body>
          <Form>
            <Row className="g-3">
              {/* Abflughafen */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Abflughafen (optional)</Form.Label>
                  <Form.Control
                    name="airport"
                    list="airport-list"
                    value={params.airport}
                    onChange={handleChange}
                    placeholder="z.B. FRA"
                  />
                  <datalist id="airport-list">
                    {GERMAN_IATAS.map((code) => (
                      <option key={code} value={code}>
                        {AIRPORTS[code] || code}
                      </option>
                    ))}
                  </datalist>
                </Form.Group>
              </Col>

              {/* Min Tage */}
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Min. Tage</Form.Label>
                  <Form.Control
                    type="number"
                    name="min_days"
                    value={params.min_days}
                    onChange={handleChange}
                    min={1}
                  />
                </Form.Group>
              </Col>

              {/* Max Tage */}
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Max. Tage</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_days"
                    value={params.max_days}
                    onChange={handleChange}
                    min={params.min_days}
                  />
                </Form.Group>
              </Col>

              {/* Budget */}
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Budget (€)</Form.Label>
                  <Form.Control
                    type="number"
                    name="budget"
                    value={params.budget}
                    onChange={handleChange}
                    min={0}
                  />
                </Form.Group>
              </Col>

              {/* Abflugmonat */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Abflugmonat (optional)</Form.Label>
                  <Form.Control
                    type="month"
                    name="departure_at"
                    value={params.departure_at}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    YYYY-MM oder leer lassen
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Hotelbudget */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Hotelbudget (€)</Form.Label>
                  <Form.Control
                    type="number"
                    name="hotel_budget"
                    value={params.hotel_budget}
                    onChange={handleChange}
                    min={0}
                    placeholder="optional"
                  />
                </Form.Group>
              </Col>

              {/* Such-Button */}
              <Col md={12} className="text-center mt-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={searchFlights}
                  disabled={loading}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : "Suchen"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* ✈️ Animation, solange noch keine Ergebnisse da sind */}
      {!loading && flights.length === 0 && (
        <div
          className="animation-wrapper"
          style={{
            width: `${logoSize}px`,
            height: `${logoSize}px`,
            "--orbit-radius-x": `${orbitRadius}px`,
            "--orbit-radius-y": `${orbitRadius}px`
          }}
        >
          <img
            src="/Fluybuy-logo.png"
            alt="Logo"
            className="animation-logo"
            style={{ width: "100%", height: "auto", borderRadius: "50%" }}
          />
          <span className="animation-plane">✈️</span>
        </div>
      )}

      {/* Ergebnisse */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <FlightList flights={flights} />  // FlightList sollte dann auch das Hotel anzeigen können
      )}
    </Container>
  );
}

export default function App() {
  return (
    <Router>
      {/* Navbar */}
      <Navbar className="skyscanner-navbar" expand="lg">
        <Container>
          <Navbar.Brand as={Link} to="/" className="mx-auto">
            <img
              src="/Fluybuy-logo.png"
              alt="Fluybuy Logo"
              className="skyscanner-logo"
            />
          </Navbar.Brand>
        </Container>
      </Navbar>

      {/* Routen */}
      <Routes>
        <Route path="/" element={<SearchPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/legal" element={<Legal />} />
      </Routes>

      {/* Footer auf allen Seiten */}
      <Footer />
    </Router>
  );
}
